/**
 * Rimettere in piedi una LINEA: una sequenza di semi-mosse conservata in forma
 * compatta (`LineStep`) e rigiocata contro la posizione da cui parte.
 *
 * È il gemello di `applyParsedOpening` ([opening.ts]) e ne segue il patto — *si
 * ferma alla prima semi-mossa che non riconosce* — con due differenze: qui le
 * catture disambiguano, e quello che torna sono le `Move` CANONICHE prodotte da
 * `getAllLegalMoves`, non i passi di partenza. Chi rigioca una linea salvata non
 * si fida di quello che gli arriva: lo risolve contro le mosse legali vere.
 *
 * Serve a tutti e tre i lati — il server lo usa per validare prima di scrivere in
 * libreria, il client per riaprire uno studio sulla damiera d'analisi.
 */

import { GameStatus, type BoardState, type LineStep, type Move } from '@draughts/shared';
import { getAllLegalMoves } from '../moves/capture-moves.js';
import { applyMove } from './move-execution.js';

/** Esito del replay: dove si è arrivati, e fin dove la linea ha retto. */
export interface ReplayedLine {
  /** La posizione dopo l'ultima semi-mossa riconosciuta. */
  board: BoardState;
  /** Le mosse vere, nell'ordine in cui sono state giocate. */
  moves: Move[];
  /** Le stesse mosse in forma compatta e CANONICA: è questa che si conserva. */
  steps: LineStep[];
}

/** Le caselle catturate, ordinate: un insieme confrontabile senza allocare un Set. */
function captureKey(squares: readonly number[]): string {
  return [...squares].sort((a, b) => a - b).join(',');
}

/**
 * Gioca i passi indicati, risolvendo ciascuno fra le mosse legali della posizione.
 *
 * Si ferma — senza lanciare — quando un passo non trova nessuna mossa legale, e
 * quando la partita è finita: `getAllLegalMoves` restituisce mosse anche su una
 * board già `DRAW` (il contatore frisone la chiude mentre le mosse ci sono),
 * ma `applyMove` le rifiuta, e un troncamento silenzioso è meglio di un'eccezione
 * dentro al caricamento di uno studio.
 *
 * Se dopo il filtro sulle catture restano PIÙ candidati si prende il primo, e non
 * è una resa: stessa partenza, stesso arrivo e stesso insieme catturato danno la
 * stessa posizione risultante: cambia solo il `path`, cioè l'animazione.
 */
export function replayLine(board: BoardState, steps: readonly LineStep[]): ReplayedLine {
  let current = board;
  const moves: Move[] = [];

  for (const step of steps) {
    if (current.status !== GameStatus.PLAYING) break;

    const candidates = getAllLegalMoves(current).filter(
      (m) => m.from === step.from && m.to === step.to,
    );
    const chosen =
      step.captures === undefined || candidates.length <= 1
        ? candidates[0]
        : candidates.find((m) => captureKey(m.captures.map((c) => c.square)) === captureKey(step.captures!));

    if (!chosen) break;
    current = applyMove(current, chosen);
    moves.push(chosen);
  }

  return { board: current, moves, steps: toLineSteps(moves) };
}

/** La forma da conservare: `captures` solo dove serve davvero, cioè dove si mangia. */
export function toLineSteps(moves: readonly Move[]): LineStep[] {
  return moves.map((m) => (
    m.captures.length > 0
      ? { from: m.from, to: m.to, captures: m.captures.map((c) => c.square) }
      : { from: m.from, to: m.to }
  ));
}
